import { useMutation, UseMutationOptions } from "@tanstack/react-query";

/**
 * Default options for cached mutations that work well with offline functionality
 */
const CACHED_MUTATION_DEFAULTS = {
  networkMode: "always" as const, // Always execute mutations, even when offline
  retry: false, // Disable automatic retries for mutations
} as const;

/**
 * Hook that provides useMutation with offline-first defaults.
 *
 * This hook applies sensible defaults for mutations that should:
 * - Execute even when offline (queued by offline system)
 * - Not automatically retry (handled by offline queue)
 * - Work seamlessly with the offline queue system
 *
 * Individual options can still be overridden by passing them explicitly.
 *
 * @example
 * ```typescript
 * // Uses cached defaults for offline support
 * const mutation = useCachedMutation({
 *   mutationFn: createClient,
 *   onSuccess: (data) => {
 *     console.log('Client created:', data);
 *   },
 * });
 *
 * // Override specific options
 * const mutation = useCachedMutation({
 *   mutationFn: updateClient,
 *   retry: 3, // Override to enable retries
 *   networkMode: "online", // Override to only work when online
 * });
 * ```
 */
export function useCachedMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(options: UseMutationOptions<TData, TError, TVariables, TContext>) {
  return useMutation({
    ...CACHED_MUTATION_DEFAULTS,
    ...options,
  });
}
